import TanStackTable from "../../../components/TanStackTable";
import { useCreateNotificationType, useDeleteNotificationType, useGetNotificationTypes, useUpdateNotificationType } from "../../../queries/notifications";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import { useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import ActiveStatus from "../../../components/Common/ActiveStatus";
import { FaTrash } from "react-icons/fa";
import { ActionButton } from "../../../components/Forms/ActionButton";
import Modal from "../../Submissions/components/Modal";
import { useForm } from "react-hook-form";
import { getAdminType } from "../../../utils/adminType";

const NotificationTypes = () => {

    const adminType = getAdminType();
    
    const { data, isPending } = useGetNotificationTypes();

    const [showCreateModal, setShowCreateModal] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        types: '',
        status: 'all'
    });

    // Applied filters state - only updated when search button is clicked
    const [appliedFilters, setAppliedFilters] = useState({
        types: '',
        status: 'all'
    });

    const { mutateAsync: updateNotificationType } = useUpdateNotificationType();
    const { mutateAsync: deleteNotificationType } = useDeleteNotificationType();
    const { mutateAsync: createNotificationType } = useCreateNotificationType();

    // Filter and search functionality
    const filteredData = useMemo(() => {
        if (!data) return [];

        return data.filter(notificationType => {
            // Types filter
            if (appliedFilters.types && !notificationType.types?.toLowerCase().includes(appliedFilters.types.toLowerCase())) {
                return false;
            }

            // Status filter
            if (appliedFilters.status !== 'all') {
                const isActive = appliedFilters.status === 'active';
                if (notificationType.active !== isActive) return false;
            }

            return true;
        });
    }, [data, appliedFilters]);

    // Handle search functionality
    const handleSearch = () => {
        // Apply current filters when search button is clicked
        setAppliedFilters({
            types: filters.types,
            status: filters.status
        });
    };

    // Reset filters
    const handleReset = () => {
        const resetState = {
            types: '',
            status: 'all'
        };
        setFilters(resetState);
        setAppliedFilters(resetState);
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            types: '',
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all',
    });

    const onSubmit = async (data) => {
        await createNotificationType({
            types: data.types,
        }, {
            onSuccess: (res) => {
                if (res.error) {
                    toast.error("Failed to create notification type");
                    return;
                }
                toast.success("Notification type created successfully");
                setShowCreateModal(false);
                reset();
            },
            onError: (error) => {
                toast.error("Failed to create notification type: " + (error.message || "Unknown error"));
            }
        });
    }

    const columnHelper = createColumnHelper();

    const handleNotificationStatusToggle = async (notificationId, currentStatus) => {
        await updateNotificationType(
            { id: notificationId, active: !currentStatus },
            {
                onSuccess: () => {
                    toast.success(`Notification status updated successfully`);
                },
                onError: (error) => {
                    toast.error("Failed to update notification status: " + (error.message || "Unknown error"));
                }
            }
        );
    };

    const handleDelete = async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this notification type?");
        if (!confirm) return;

        await deleteNotificationType(id, {
            onSuccess: () => {
                toast.success("Notification type deleted successfully");
            },
            onError: (error) => {
                toast.error("Failed to delete notification type: " + (error.message || "Unknown error"));
            }
        });
    };

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('types', {
            header: 'Type',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('createdAt', {
            header: 'Created At',
            align: 'center',
            cell: info => (
                <div className='text-center'>
                    {new Date(info.getValue()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                    <br />
                    <small className='text-secondary'>
                        {new Date(info.getValue()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </small>
                </div>
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            align: 'center',
            enableColumnFilter: false,
            cell: info => {
                const review = info.row.original;
                return (
                    <div className='flex items-center justify-center gap-2 flex-wrap'>
                        <div className='min-w-fit'>
                            <ActiveStatus
                                active={review.active}
                                setActive={() => handleNotificationStatusToggle(review._id, review.active)}
                                label=""
                                innerActiveText="Show"
                                innerInactiveText="Hide"
                                hideLabel={true}
                            />
                        </div>
                        {
                            adminType === 'superadmin' && (
                                <button
                                    className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                                    onClick={() => handleDelete(review._id)}
                                    title="Delete Review"
                                >
                                    <FaTrash className="fill-chipRed" />
                                </button>
                            )
                        }
                    </div>
                )
            },
        }),
    ], [updateNotificationType, deleteNotificationType]);

    if (isPending) {
        return (
            <div>Loading...</div>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">

            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Add Notification Type">
                <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-full">
                        <h2 className="text-lg font-bold mb-4">Add Notification Type</h2>
                        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
                            <div className="flex flex-col gap-0">
                                <input
                                    type="text"
                                    placeholder="Enter notification type"
                                    className="border border-gray-300 rounded-md p-2"
                                    {...register("types", {
                                        required: "Notification type is required",
                                        pattern: {
                                            value: /^[^\s]*$/,
                                            message: "Use underscore _ instead of whitespace between words"
                                        }
                                    })}
                                />
                                {errors.types && <span className="text-red">{errors.types.message}</span>}
                            </div>
                            <div className="flex justify-end">
                                <ActionButton
                                    variant={'primary'}
                                    type={'submit'}
                                    text={'Add Notification Type'}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>

            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)]"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch();
                }}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="all">All</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="types" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Notification Type
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="types"
                                    value={filters.types}
                                    onChange={(e) => setFilters(prev => ({ ...prev, types: e.target.value }))}
                                    placeholder="Search by notification type..."
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={handleReset}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={() => { }}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full">
                    <div className="w-full flex items-center justify-between px-6 mb-4">
                        <h1 className="text-lg font-bold">Notification Types</h1>
                        <div>
                            <ActionButton
                                variant={'reset'}
                                text={'Add Notification Type'}
                                handleSubmit={() => setShowCreateModal(true)}
                            />
                        </div>
                    </div>
                    <div className='px-6 text-base'>
                        {filteredData && filteredData.length > 0 ? (
                            <TanStackTable
                                data={filteredData}
                                columns={columns}
                                enablePagination={true}
                            />
                        ) : (
                            <div className="pb-4">
                                No Notification Types available
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={2000}
            />
        </main>
    );
}

export default NotificationTypes;